import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, Building2, Sparkles, ArrowLeft, CheckCircle, KeyRound } from 'lucide-react';
import { getBackendUrl } from '../utils/apiUrl';

const TenantLoginPage = ({ tenantSubdomain, defaultTab = 'customer' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check if we're on the /admin route - if so, force admin tab only
  const isAdminRoute = location.pathname.includes('/admin');
  // Check if we're on customer-facing routes (/, /login, /customer) - if so, force customer only
  const isCustomerRoute = location.pathname === '/' || location.pathname === '/login' || location.pathname.includes('/customer');
  const effectiveDefaultTab = isAdminRoute ? 'admin' : 'customer';
  
  const [activeTab, setActiveTab] = useState(effectiveDefaultTab);
  const [loginMethod, setLoginMethod] = useState('password'); // Default to password for admin
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Check for magic link token or SSO token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    const ssoToken = searchParams.get('sso_token');
    
    if (ssoToken && isAdminRoute) {
      // Shopify admin SSO — store token and verify via /api/auth/me
      verifyAdminSSO(ssoToken);
    } else if (ssoToken) {
      verifySSOToken(ssoToken);
    } else if (token && type === 'magic') {
      verifyMagicLink(token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch tenant info
  useEffect(() => {
    const fetchTenantInfo = async () => {
      // If no tenantSubdomain provided, skip fetching and set loading to false
      if (!tenantSubdomain) {
        setTenantLoading(false);
        return;
      }
      
      try {
        // Determine backend URL based on environment
        const hostname = window.location.hostname;
        const backendUrl = getBackendUrl();
        
        const response = await fetch(
          `${backendUrl}/api/auth/tenant-info/${tenantSubdomain}`
        );
        if (response.ok) {
          const data = await response.json();
          setTenantInfo(data);
        } else {
          setError('Tenant not found');
        }
      } catch (err) {
        console.error('Error fetching tenant info:', err);
        setError('Failed to load tenant information');
      } finally {
        setTenantLoading(false);
      }
    };
    
    fetchTenantInfo();
  }, [tenantSubdomain]);

  const verifyMagicLink = async (token) => {
    setLoading(true);
    try {
      // Determine backend URL based on environment
      const backendUrl = getBackendUrl();
      
      const response = await fetch(
        `${backendUrl}/api/auth/verify-magic-link`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, subdomain: tenantSubdomain })
        }
      );

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('plateos_token', data.access_token);
        localStorage.setItem('plateos_user', JSON.stringify(data.user));
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('tenant_id', data.user.tenant_id);
        // Route based on role — tenant_admin/admin go to /admin, customers go to /customer
        const userRole = data.user.role || 'customer';
        if (userRole === 'tenant_admin' || userRole === 'admin' || userRole === 'super_admin') {
          navigate('/admin');
        } else {
          navigate('/customer');
        }
      } else {
        setError(data.detail || 'Invalid or expired link');
      }
    } catch (err) {
      setError('Failed to verify login link');
    } finally {
      setLoading(false);
    }
  };

  // Shopify admin SSO — token is already a valid PlateOS JWT
  const verifyAdminSSO = async (ssoToken) => {
    setLoading(true);
    setError('');
    try {
      // Store the token
      localStorage.setItem('plateos_token', ssoToken);

      // Verify and fetch user profile
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${ssoToken}` }
      });

      if (response.ok) {
        const userData = await response.json();
        // Store in ALL localStorage keys (auth guards check both 'token'/'user' and 'plateos_*')
        localStorage.setItem('plateos_token', ssoToken);
        localStorage.setItem('plateos_user', JSON.stringify(userData));
        localStorage.setItem('token', ssoToken);
        localStorage.setItem('user', JSON.stringify(userData));
        if (userData.tenant_id) {
          localStorage.setItem('tenant_id', userData.tenant_id);
        }
        // Honor `?next=/admin/...` deep-link for Shopify embedded buttons
        // (Manage Connections, View All Orders, Manage Selling Plans, etc.).
        // Sanitised to internal /admin paths to prevent open-redirect — anything
        // off-domain or outside /admin falls back to the dashboard root.
        const rawNext = searchParams.get('next');
        let dest = '/admin';
        if (rawNext && /^\/admin(\/|$)/.test(rawNext) && !rawNext.includes('//')) {
          dest = rawNext;
        }
        // Use window.location.href (full page reload) so AuthProvider re-initializes from localStorage
        window.location.href = dest;
      } else {
        localStorage.removeItem('plateos_token');
        setError('SSO session expired. Please log in manually.');
      }
    } catch (err) {
      localStorage.removeItem('plateos_token');
      setError('SSO login failed. Please log in with your credentials.');
    } finally {
      setLoading(false);
    }
  };


  const verifySSOToken = async (ssoToken) => {
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(
        `${backendUrl}/api/customer/verify-sso-token?sso_token=${encodeURIComponent(ssoToken)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        sessionStorage.setItem('customerSession', JSON.stringify({
          token: data.session_token,
          store_slug: data.store_slug,
          store_name: data.store_name,
          customer: data.customer,
          tenant: data.tenant,
          expires_at: data.expires_at
        }));
        const slug = data.store_slug;
        if (slug) {
          navigate(`/customers/${slug}/dashboard`, { replace: true });
        } else {
          navigate('/customers/dashboard', { replace: true });
        }
      } else {
        setError(data.detail || 'SSO login failed. Please use email login.');
      }
    } catch (err) {
      setError('SSO login failed. Please use email login.');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Determine backend URL based on environment
      const backendUrl = getBackendUrl();
      
      const response = await fetch(
        `${backendUrl}/api/auth/request-magic-link`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            subdomain: tenantSubdomain
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess('Check your email! We sent you a login link that expires in 30 minutes.');
      } else {
        setError(data.detail || 'Failed to send login link');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Determine backend URL based on environment
      const backendUrl = getBackendUrl();
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(
        `${backendUrl}/api/auth/tenant-login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            subdomain: tenantSubdomain || 'plateos-super-admin', // Use super admin tenant in preview mode
            login_type: activeTab
          }),
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);

      // Clone response for error handling
      const resClone = response.clone();
      let data;
      
      try {
        data = await response.json();
      } catch (parseError) {
        const textResponse = await resClone.text();
        console.error('Failed to parse response:', textResponse);
        setError('Server error. Please try again.');
        setLoading(false);
        return;
      }

      if (response.ok) {
        localStorage.setItem('plateos_token', data.access_token);
        localStorage.setItem('plateos_user', JSON.stringify(data.user));
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('tenant_id', data.user.tenant_id);
        
        // If tenant is suspended, redirect to billing/reactivation instead of dashboard
        if (data.user?.is_suspended || data.tenant?.status === 'suspended') {
          window.location.href = '/admin/billing?reactivate=true';
          return;
        }
        
        // Force a page reload to ensure clean state after login
        // Super admins and tenant admins should go to /admin
        const isAdminRole = data.user.role === 'tenant_admin' || data.user.role === 'admin' || data.user.role === 'super_admin';
        if (activeTab === 'admin' || isAdminRole) {
          window.location.href = '/admin';
        } else {
          window.location.href = '/customer';
        }
      } else {
        // Handle specific error messages
        const errorMessage = data.detail || 'Login failed';
        if (errorMessage.toLowerCase().includes('invalid email or password')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (errorMessage.toLowerCase().includes('not found')) {
          setError('Account not found. Please check your email address.');
        } else if (errorMessage.toLowerCase().includes('deactivated')) {
          setError('This account has been deactivated. Please contact support.');
        } else if (errorMessage.toLowerCase().includes('admin access')) {
          setError('You do not have admin access. Please use the Customer login instead.');
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        setError('Incorrect username or password. Please check and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError('');
    setForgotPasswordSuccess('');
    
    try {
      // Determine backend URL based on environment
      const backendUrl = getBackendUrl();
      
      const response = await fetch(
        `${backendUrl}/api/auth/tenant/forgot-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: forgotPasswordEmail,
            subdomain: tenantSubdomain
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setForgotPasswordSuccess('If an account exists with this email, you will receive password reset instructions.');
        setForgotPasswordEmail('');
      } else {
        // Don't reveal if email exists or not for security
        setForgotPasswordSuccess('If an account exists with this email, you will receive password reset instructions.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  // Only show tenant not found error if tenantSubdomain was provided and tenant wasn't found
  if (tenantSubdomain && !tenantInfo && !tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Tenant Not Found</h2>
              <p className="text-gray-600">
                The subdomain &quot;{tenantSubdomain}&quot; doesn&apos;t exist or is inactive.
              </p>
              <Button 
                className="mt-6"
                onClick={() => window.location.href = 'https://plateos.io'}
              >
                Go to PlateOS
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full filter blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo and Company Name */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 shadow-lg shadow-teal-500/25 mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">{tenantInfo?.company_name}</h1>
            <p className="text-slate-400 text-sm mt-1">
              Powered by PlateOS
            </p>
          </div>

          {/* Login Card */}
          <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-xl shadow-2xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-xl text-white text-center">
                {isAdminRoute ? 'Admin Login' : 'Customer Login'}
              </CardTitle>
              <CardDescription className="text-slate-400 text-center">
                {isAdminRoute ? 'Sign in to manage your business' : 'Sign in to your customer account'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isAdminRoute && searchParams.get('session') === 'expired' && <p role="status" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Session expired. Please sign in again.</p>}
              {/* No tabs shown - routes determine the login type */}
              {/* Customer-facing routes (/, /login, /customer) only show customer login */}
              {/* Admin route (/admin) only shows admin login */}

              {/* Forgot Password Form */}
              {showForgotPassword ? (
                <div className="space-y-4">
                  <button
                    onClick={() => { setShowForgotPassword(false); setError(''); setForgotPasswordSuccess(''); }}
                    className="flex items-center text-sm text-slate-400 hover:text-white transition-colors mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to login
                  </button>
                  
                  <div className="text-center mb-6">
                    <KeyRound className="h-12 w-12 text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-white">Reset Password</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Enter your email and we&apos;ll send you reset instructions
                    </p>
                  </div>
                  
                  {forgotPasswordSuccess && (
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      {forgotPasswordSuccess}
                    </div>
                  )}
                  
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}
                  
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-slate-300">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="your@email.com"
                          value={forgotPasswordEmail}
                          onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                          required
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>
                  </form>
                </div>
              ) : (
                <>
                  {/* Customer Login - Magic Link or Password */}
                  {activeTab === 'customer' && !isAdminRoute && (
                <>
                  {success ? (
                    <div className="text-center py-6">
                      <CheckCircle className="h-12 w-12 text-teal-500 mx-auto mb-4" />
                      <p className="text-white font-medium mb-2">Email Sent!</p>
                      <p className="text-slate-400 text-sm">{success}</p>
                      <Button 
                        variant="ghost" 
                        className="mt-4 text-teal-400"
                        onClick={() => { setSuccess(''); setFormData({ email: '', password: '' }); }}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to login
                      </Button>
                    </div>
                  ) : loginMethod === 'magic' ? (
                    <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                            required
                          />
                        </div>
                      </div>

                      {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {error}
                        </div>
                      )}

                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white h-11"
                        disabled={loading}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Send Magic Link
                          </>
                        )}
                      </Button>

                      <p className="text-center text-slate-500 text-xs">
                        We&apos;ll email you a secure link that expires in 30 minutes
                      </p>

                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-700"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-slate-800 text-slate-500">or</span>
                        </div>
                      </div>

                      <Button 
                        type="button"
                        variant="ghost" 
                        className="w-full text-slate-400 hover:text-white"
                        onClick={() => setLoginMethod('password')}
                      >
                        <Lock className="h-4 w-4 mr-2" />
                        Sign in with password
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handlePasswordLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-slate-300">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="pl-10 pr-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {error}
                        </div>
                      )}

                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white h-11"
                        disabled={loading}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Sign In'
                        )}
                      </Button>

                      <Button 
                        type="button"
                        variant="ghost" 
                        className="w-full text-slate-400 hover:text-white"
                        onClick={() => { setLoginMethod('magic'); setError(''); }}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Use magic link instead
                      </Button>
                    </form>
                  )}
                </>
              )}

              {/* Admin Login - Password only */}
              {(activeTab === 'admin' || isAdminRoute) && (
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email" className="text-slate-300">Admin Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="admin-email"
                        type="email"
                        placeholder="admin@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="admin-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 pr-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white h-11"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Sign In as Admin'
                    )}
                  </Button>
                  
                  {/* Forgot Password Link */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setError(''); }}
                      className="text-sm text-slate-400 hover:text-teal-400 transition-colors"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              )}
              </>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-slate-500 text-xs mt-6">
            © {new Date().getFullYear()} {tenantInfo?.company_name}. Powered by PlateOS.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantLoginPage;
